import { Controller } from "@hotwired/stimulus";

// Companion to the autocomplete controller for IATA airport inputs.
//
//  - Once the clerk has typed an exact IATA code (e.g. "FRA") the dropdown is
//    closed by committing the match, exactly as if it had been clicked, so the
//    input (and the hidden field, if any) hold the code and
//    "autocomplete.change" fires.
//  - The committed airport is confirmed with a small, subtle hint at the right
//    edge of the input ("Frankfurt am Main Airport"), so the clerk can see the
//    code is the airport they meant. It also appears for codes that were
//    prefilled, and after a programmatic change (dispatch
//    "airport-hint:refresh" on the input).
//
// Usage — on the same element as the autocomplete controller:
//   data-controller="autocomplete airport-exact-match"
//   data-action="loadend->airport-exact-match#close"
//
// "loadend" is dispatched by stimulus-autocomplete after every results fetch.
// The results list marks each option with data-airport-name / data-airport-city.

// code -> "Name, City" (or null when unknown), shared by all inputs on the page.
const labelCache = new Map();

// Space kept free at the input's right edge (px): the clear (x) button of a
// focused search field.
const HINT_END_GAP = 36;

export default class AirportExactMatchController extends Controller {
  connect() {
    this.input = this.element.querySelector(
      '[data-autocomplete-target="input"]'
    );
    if (!this.input) return;

    this.onChange = (event) => this.showForOption(event.detail?.selected);
    this.onInput = () => this.clearIfEdited();
    this.onRefresh = () => this.refresh();
    this.element.addEventListener("autocomplete.change", this.onChange);
    this.input.addEventListener("input", this.onInput);
    this.input.addEventListener("airport-hint:refresh", this.onRefresh);

    this.refresh();
  }

  disconnect() {
    if (!this.input) return;

    this.element.removeEventListener("autocomplete.change", this.onChange);
    this.input.removeEventListener("input", this.onInput);
    this.input.removeEventListener("airport-hint:refresh", this.onRefresh);
    this.hint?.remove();
  }

  close() {
    const autocomplete = this.application.getControllerForElementAndIdentifier(
      this.element,
      "autocomplete"
    );
    if (!autocomplete) return;

    // The results may belong to an earlier keystroke: only act when what is in
    // the field *now* is the code that was found.
    const typed = this.typedCode();
    if (!typed) return;

    const exact = Array.from(
      autocomplete.resultsTarget.querySelectorAll("[data-autocomplete-value]")
    ).find(
      (option) => option.dataset.autocompleteValue.toUpperCase() === typed
    );

    if (!exact) return;

    // commit() moves the focus into the input. The results can arrive after the
    // clerk has already tabbed on (the autocomplete fetches 300 ms after the last
    // keystroke), and pulling the focus back would swallow that Tab: take the
    // value without committing then.
    if (document.activeElement === autocomplete.inputTarget) {
      autocomplete.commit(exact);
      return;
    }

    if (autocomplete.hasHiddenTarget)
      autocomplete.hiddenTarget.value = exact.dataset.autocompleteValue;
    autocomplete.hideAndRemoveOptions();
    this.showForOption(exact);
  }

  // ── Hint ───────────────────────────────────────────────────────────────

  // Looks the current value up (cached) — for prefilled and swapped codes.
  async refresh() {
    const code = this.typedCode();
    if (!code) {
      this.setHint(null);
      return;
    }

    const label = await this.labelFor(code);
    // The field may have changed while the lookup was running.
    if (this.typedCode() === code) this.setHint(label, code);
  }

  showForOption(option) {
    const code = this.typedCode();
    if (!option || !code) return;

    const label = this.labelOf(option);
    labelCache.set(code, label);
    this.setHint(label, code);
  }

  clearIfEdited() {
    if (this.hintCode && this.typedCode() !== this.hintCode) this.setHint(null);
  }

  async labelFor(code) {
    if (labelCache.has(code)) return labelCache.get(code);

    let label = null;
    try {
      const url = `${
        this.element.dataset.autocompleteUrlValue
      }?q=${encodeURIComponent(code)}`;
      const response = await fetch(url, { headers: { Accept: "text/html" } });
      if (response.ok) {
        const doc = new DOMParser().parseFromString(
          await response.text(),
          "text/html"
        );
        const option = Array.from(
          doc.querySelectorAll("[data-autocomplete-value]")
        ).find((el) => el.dataset.autocompleteValue.toUpperCase() === code);
        label = option ? this.labelOf(option) : null;
      }
    } catch (error) {
      // No hint is better than a broken form.
    }
    labelCache.set(code, label);
    return label;
  }

  // eslint-disable-next-line class-methods-use-this
  labelOf(option) {
    const { airportName, airportCity: city } = option.dataset;
    // "Frankfurt am Main Airport" -> "Frankfurt am Main": the suffix is noise here.
    const name = (airportName || "").replace(/\s*\bAirport\b/gi, "").trim();
    if (!name) return city || null;
    return city && !name.toLowerCase().includes(city.toLowerCase())
      ? `${name}, ${city}`
      : name;
  }

  typedCode() {
    const typed = this.input.value.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(typed) ? typed : null;
  }

  // The hint sits inside the input's box, right-aligned and non-interactive;
  // the typed code stays on the left, so the two never overlap in practice and
  // the label is truncated rather than wrapped.
  setHint(label, code = null) {
    if (!label) {
      this.hint?.remove();
      this.hint = null;
      this.hintCode = null;
      return;
    }

    if (!this.hint) {
      if (getComputedStyle(this.element).position === "static")
        this.element.style.position = "relative";
      this.hint = document.createElement("span");
      this.hint.setAttribute("aria-hidden", "true");
      this.hint.className =
        "pointer-events-none absolute flex items-center justify-end truncate text-xs font-normal normal-case tracking-normal text-gray-400";
      this.element.appendChild(this.hint);
    }

    const { offsetTop, offsetLeft, offsetWidth, offsetHeight } = this.input;
    Object.assign(this.hint.style, {
      top: `${offsetTop}px`,
      height: `${offsetHeight}px`,
      left: `${offsetLeft + offsetWidth * 0.3}px`,
      // Right edge stays clear of the browser's clear (x) button on focus.
      width: `${offsetWidth * 0.7 - HINT_END_GAP}px`,
    });
    this.hint.textContent = label;
    this.hintCode = code;
  }
}
